import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import Breadcrumbs from 'components/Core/Breadcrumbs';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import trackAction from 'utils/tracking';

import NewsCard from 'components/NewsCard';
import Pagination from 'components/Pagination';

const NewsIndex = (props) => {
  const {
    page: {
      url,
      newsTitle,
      newsSubtitle,
      breadcrumbs,
      newsArticles: { pagination, items },
    },
  } = props;
  const router = useRouter();

  const paginate = async (data) => {
    const page = data.selected + 1;
    trackAction('news-overview', `${page}`, 'page');
    const pathAs = `${url}?page=${page}`;
    router
      .replace(
        {
          pathname: '/[...slug]',
          query: { slug: url, page: `${page}` },
        },
        pathAs,
      )
      .then(() => {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
      });
  };

  const { totalPages, prevPage, nextPage } = pagination;

  const onItemClick = (itemLabel) => {
    trackAction('news-overview', itemLabel, 'article-click');
  };

  return (
    <div className="container mx-auto px-4 u-block-margins">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className="mb-4 md:mb-8">
        <h1 className="text-secondary mb-6">{newsTitle}</h1>
        <h2 className="mb-4">{newsSubtitle}</h2>
      </div>
      <div className="flex flex-wrap -mx-4">
        {items.map((newsItem) => {
          return (
            <div
              key={newsItem.url}
              className="flex-full max-w-full sm:flex-1/2 sm:max-w-1/2 xl:flex-1/3 xl:max-w-1/3 px-4"
            >
              <NewsCard {...newsItem} onClick={() => onItemClick(newsItem.shortTitle)} />
            </div>
          );
        })}
      </div>
      {(prevPage || nextPage) && (
        <div className="my-4">
          <Pagination onPageClick={(data) => paginate(data)} totalPages={totalPages} initialPage={0} />
        </div>
      )}
    </div>
  );
};

// The data returned here will be sent as `props` to the component
// TODO: Let's see if we need this here or if we can ultimately adopt this directly in the lib/api calls
// That will depend on how different these calls are going to be along the way.
NewsIndex.getCustomInitialProps = async ({ id, contentType, hostname, previewData, currentPage }) => {
  const query = requireQueryByName('NewsIndex');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
    currentPage,
  });

  return {
    page,
  };
};

NewsIndex.defaultProps = {
  page: {
    children: [],
  },
};

NewsIndex.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string.isRequired,
    newsTitle: PropTypes.string.isRequired,
    newsSubtitle: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
    newsArticles: PropTypes.shape({
      items: PropTypes.arrayOf(NewsCard),
      pagination: PropTypes.shape({
        total: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired,
        prevPage: PropTypes.number,
        nextPage: PropTypes.number,
        totalPages: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
      }),
    }),
  }),
};

export default NewsIndex;
